import { cn, Radio, RadioGroup, Tooltip } from '@heroui/react';
import { AnimatePresence, motion } from 'framer-motion';
import html2canvas from 'html2canvas';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import InstagramOverlay from '../../../assets/images/safe-zone-overlays/instagram-reels-overlay.png';
import InstagramSafeZoneOverlay from '../../../assets/images/safe-zone-overlays/instagram-reels-safe-zone-overlay.png';
import TiktokOverlay from '../../../assets/images/safe-zone-overlays/tiktok-overlay.png';
import TiktokSafeZoneOverlay from '../../../assets/images/safe-zone-overlays/tiktok-safe-zone-overlay.png';
import YoutubeOverlay from '../../../assets/images/safe-zone-overlays/youtube-shorts-overlay.png';
import YoutubeSafeZoneOverlay from '../../../assets/images/safe-zone-overlays/youtube-shorts-safe-zone-overlay.png';
import { getSafeZoneSpec, SafeZoneSpec } from '../../../data/safe-zones';
import { useCreatorToolDownload } from '../../../hooks/useCreatorToolDownload';
import { useSoftGate } from '../../../hooks/useSoftGate';
import { Icon } from '../../various/Icon';
import { downloadCompositePng, downloadOverlayPng } from '../safeZoneOverlay';
import { SafeZoneOverlaySvg } from '../SafeZoneOverlaySvg';
import { SafeZoneScreenEmptyState } from './SafeZoneScreenEmptyState';
import { SafeZoneScreenImage } from './SafeZoneScreenImage';
import { SafeZoneScreenVideo } from './SafeZoneScreenVideo';

const OVERLAYS = {
  instagram: InstagramOverlay,
  tiktok: TiktokOverlay,
  youtube: YoutubeOverlay,
};

const SAFE_ZONE_OVERLAYS = {
  instagram: InstagramSafeZoneOverlay,
  tiktok: TiktokSafeZoneOverlay,
  youtube: YoutubeSafeZoneOverlay,
};

type PngPlatform = keyof typeof OVERLAYS;

const PNG_PLATFORM_LABELS: Record<PngPlatform, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
};

interface SafeZoneScreenProps {
  defaultPlatform?: PngPlatform;
  /** Data-built overlay (generated checker pages). When set, the platform switcher is hidden. */
  overlay?: SafeZoneSpec;
  /** Platforms offered in the switcher for PNG overlays. */
  platforms?: PngPlatform[];
  /** Prompt sign-up as soon as media is uploaded. Generated pages pass `false` so the preview stays free. */
  softGateOnUpload?: boolean;
}

export const SafeZoneScreen = ({
  defaultPlatform = 'instagram',
  overlay,
  platforms = ['instagram', 'tiktok', 'youtube'],
  softGateOnUpload = true,
}: SafeZoneScreenProps = {}) => {
  const captureRef = useRef(null);

  const [file, setFile] = useState<File | null>(null);
  const [activeOverlay, setActiveOverlay] = useState<keyof typeof OVERLAYS>(defaultPlatform);
  const [shouldShowSafeZone, setShouldShowSafeZone] = useState(false);

  const activeOverlayData = (shouldShowSafeZone ? SAFE_ZONE_OVERLAYS : OVERLAYS)[activeOverlay];
  /** Spec behind the transparent overlay download: the data overlay, or the measured spec of the PNG platform. */
  const downloadSpec = overlay ?? getSafeZoneSpec(`${activeOverlay}-safe-zone-checker`);
  const aspectRatio = overlay ? `${overlay.canvas.width} / ${overlay.canvas.height}` : '9 / 16';

  const filePreviewUrl = useMemo(() => {
    return file ? URL.createObjectURL(file) : null;
  }, [file]);

  useEffect(() => {
    return () => {
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    };
  }, [filePreviewUrl]);

  const resetUploadedMedia = useCallback(() => {
    setFile(null);
    setShouldShowSafeZone(false);
  }, []);

  const { triggerSoftGate } = useSoftGate({
    enabled: softGateOnUpload,
    onReset: resetUploadedMedia,
  });
  const { requestDownload } = useCreatorToolDownload({
    onSoftGateReset: softGateOnUpload ? resetUploadedMedia : undefined,
  });

  const isImageFile = file?.type.startsWith('image/');
  const isVideoFile = file?.type.startsWith('video/');

  const handleUploadedFile = useCallback(
    (next: File | null | undefined) => {
      if (!next) return;
      setFile(next);
      // Soft gate: once user uploads media, prompt sign up (hand-written checkers only).
      if (softGateOnUpload) triggerSoftGate();
    },
    [softGateOnUpload, triggerSoftGate],
  );

  const handleDownload = async () => {
    if (!requestDownload()) return;

    const element = captureRef.current as HTMLElement | null;

    if (!element) {
      return;
    }

    if (overlay) {
      const media = element.querySelector<HTMLImageElement | HTMLVideoElement>('video, img');
      if (media) downloadCompositePng(overlay, media, (file?.name ?? 'capture').replace(/\.[^.]+$/, ''));
      return;
    }

    const canvas = await html2canvas(element, { useCORS: true });

    const imageURL = canvas.toDataURL('image/png');

    const link = document.createElement('a');
    link.href = imageURL;
    link.download = `${file?.name ?? 'capture'}.png`;
    link.click();
  };

  const handleOverlayDownload = () => {
    if (!downloadSpec || !requestDownload()) return;
    downloadOverlayPng(downloadSpec);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': [],
      'video/*': [],
    },
    onDrop: (files) => {
      handleUploadedFile(files[0]);
    },
    multiple: false,
  });

  return (
    <div className="flex flex-col items-center gap-3">
      {!overlay && platforms.length > 1 && (
        <RadioGroup
          value={activeOverlay}
          orientation="horizontal"
          onValueChange={(value) => setActiveOverlay(value as PngPlatform)}
        >
          {platforms.map((platform) => (
            <Radio key={platform} value={platform}>
              {PNG_PLATFORM_LABELS[platform]}
            </Radio>
          ))}
        </RadioGroup>
      )}
      <div className={cn('w-full', overlay && overlay.canvas.width > overlay.canvas.height ? 'max-w-2xl' : 'max-w-80')}>
        {filePreviewUrl && (
          <div className="flex w-full justify-between gap-2 p-2">
            {overlay ? (
              <span className="text-xs text-foreground-500">Safe area outlined in green</span>
            ) : (
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-foreground"
                onClick={() => setShouldShowSafeZone(!shouldShowSafeZone)}
              >
                <Icon icon={shouldShowSafeZone ? 'eyeCrossed' : 'eye'} size={12} />
                {shouldShowSafeZone ? 'Hide' : 'Show'} safe zone
              </button>
            )}
            <div className="flex gap-3">
              <button type="button" className="flex items-center gap-1 text-xs text-foreground" {...getRootProps()}>
                <input {...getInputProps()} />
                <Icon icon="addImage" size={16} />
                Select
              </button>
              <Tooltip content="Download" isDisabled={!filePreviewUrl}>
                <button
                  type="button"
                  className={cn('text-foreground', { 'cursor-not-allowed opacity-50': !filePreviewUrl })}
                  disabled={!filePreviewUrl}
                  onClick={handleDownload}
                >
                  <Icon icon="download" size={16} />
                </button>
              </Tooltip>
            </div>
          </div>
        )}
        <div className="relative w-full">
          <div ref={captureRef} className="relative w-full border border-foreground-300">
            <div className="w-full" style={{ aspectRatio }}>
              {filePreviewUrl && isImageFile && <SafeZoneScreenImage src={filePreviewUrl} />}
              {filePreviewUrl && isVideoFile && (
                <SafeZoneScreenVideo src={filePreviewUrl} activeOverlay={overlay ? 'data' : activeOverlay} />
              )}
              {!filePreviewUrl && <SafeZoneScreenEmptyState onUploadFile={handleUploadedFile} />}
            </div>
            {overlay ? (
              <SafeZoneOverlaySvg spec={overlay} className="pointer-events-none absolute inset-0 h-full w-full" />
            ) : (
              <>
                <AnimatePresence>
                  <motion.img
                    src={activeOverlayData.src}
                    className={cn('pointer-events-none absolute inset-0 w-full rounded-none')}
                    exit={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    initial={{ opacity: 0 }}
                    key={activeOverlayData.src}
                    alt="Instagram Overlay"
                  />
                </AnimatePresence>
                <div className="flex h-14 w-full items-center justify-center bg-black text-white">
                  <Icon icon={activeOverlay} size={28} />
                </div>
              </>
            )}
          </div>
        </div>
        {downloadSpec && (
          <button
            type="button"
            onClick={handleOverlayDownload}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-medium border border-foreground-300 px-3 py-2 text-sm font-medium text-foreground hover:bg-foreground-100"
          >
            <Icon icon="download" size={16} />
            Download transparent overlay PNG ({downloadSpec.canvas.width}×{downloadSpec.canvas.height})
          </button>
        )}
      </div>
    </div>
  );
};
